import React, { useCallback, useEffect, useState } from 'react';

import { Alert, Box, Card, CardContent, Chip, CircularProgress, LinearProgress, Typography, useTheme } from "@mui/material";
import { TrendUpIcon } from '../../../components/icons';
import { tokens } from '../../../theme';
import apiService from '../../../service/Api';
import { AxiosError } from 'axios';
import { PilotProps } from '../types/view-pilot';

interface MonthlyAssignmentProps {
  pilot: PilotProps;
};

interface PerformanceProps {
  month: string;
  year: number;
  month_number: number;
  assignment_count: number;
  percentage: number;
};

interface Params {
  crew_member: number | undefined;
};

export const MonthlyAssignments: React.FC<MonthlyAssignmentProps> = ({ pilot }) => {
    const [total, setTotal] = useState<number>(0);
    const [performance, setPerfomance] = useState<PerformanceProps[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);

    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const backgroundColor = colors.primary[800] || "#A9B2C3";

    const fetchPilotAssignments = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
          const params: Params = {
            crew_member: pilot?.id
          };

          const response = await apiService.get<{
            total_assignments: number;
            monthly_performance: PerformanceProps[];
          }>("/api/call-sheets/callsheet/monthly-performance/", params);

          setPerfomance(response.data.monthly_performance);
          setTotal(response.data.total_assignments);
        } catch (error) {
        if (error instanceof AxiosError) {
          const errorMessage = error.response?.data?.message || "Failed to load past assignments. Please try again.";
          setError(errorMessage);
        } else {
          console.log({ error }, 'fetchPastAssignments error');
        }
        } finally {
          setLoading(false);
        }
    }, [pilot]);

    useEffect(() => {
      if (pilot) {
        fetchPilotAssignments();
      }
    }, [fetchPilotAssignments, pilot]);

    return (
        <Card sx={{ borderRadius: 2 , boxShadow: 0, height: "100%" }}>
          <CardContent>
            <Box
              display={'flex'}
              alignItems={'center'}
              sx={{ pb: '8px', borderBottom: 1, borderColor: 'divider', mb: '8px' }}
              gap={'4px'}>
              <TrendUpIcon color="disabled" sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Monthly Performance
              </Typography>
            </Box>

            <Box sx={{maxHeight: "180px", overflowY: "scroll", scrollbarColor: "#888 transparent", scrollbarWidth: "thin" }}>
              {loading ? (
                <CircularProgress color={'primary'} size={16} />
                ) : error ? (
                  <Alert severity="error">{error}</Alert>
                ) : performance?.length === 0 ? (
                  <Chip
                    size={"small"}
                    sx={{ borderRadius: 1, textTransform: "uppercase" }}
                    label={<i>No assignments for {pilot.name} yet!</i>}
                  />
                ) : (
                performance?.map(perf => {
                  const progress = perf.percentage;
                  return (
                    <Box key={perf.month} sx={{ padding: 1, mb: 1, borderRadius: 2, bgcolor: backgroundColor }}>
                      <Box display={'flex'} alignItems={'center'} sx={{ mb: 0.4 }} justifyContent={'space-between'}>
                        <Typography variant={'subtitle2'} sx={{ fontWeight: 600 }}>{perf.month}</Typography>
                        <Typography variant={'body2'}>{`${perf.assignment_count}/${total} assignments`}</Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                      />
                    </Box>
                  );
                })    
              )}
            </Box>
          </CardContent>
        </Card>
    );
};

export default MonthlyAssignments;
